import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useImageOptimizationContext } from 'ui/contexts/ImageOptimizationContext';
import styles from './Picture.module.scss';
import { useDebouncedResize } from './useDebouncedResize';
import { computeFocalObjectPosition } from './computeFocalObjectPosition';

export interface FocalPoint {
	left: number;
	top: number;
}

export interface PictureProps {
	url: string;
	altText: string;
	focalPoint?: FocalPoint;
	// Width and height or imageRatio should be used when you know the image's dimensions, to tell the browser what real estate should be reserved for the image.
	// Width and height overrides imageRatio. You should always use specific width and height if they are known and omit imageRatio.
	// ImageRatio should never be used with width and/or height.
	width?: number;
	height?: number;
	sizes?: string;
	lazyload?: boolean;
	imageRatio?: number;
	className?: string;
	imageCropping?: 'cover' | 'contain';
	fetchpriority?: 'high' | 'low' | 'auto';
	style?: 'round';
}

export const UI_Picture = ({
	sizes,
	url,
	altText,
	lazyload = true,
	focalPoint,
	width,
	height,
	imageRatio,
	className,
	imageCropping = 'cover',
	fetchpriority,
	style,
}: PictureProps) => {
	const pictureRef = useRef<HTMLPictureElement>(null);
	const ImageOptimization = useImageOptimizationContext();

	// Container size from the <picture> element
	const [autoSizes, setAutoSizes] = useState<{ width: number; height: number } | null>(null);
	// Intrinsic (true) image size from either props or naturalWidth/Height
	const [intrinsicSize, setIntrinsicSize] = useState<{ w: number; h: number } | null>(null);
	const [objectPosition, setObjectPosition] = useState<string>('50% 50%');

	const getContainerSize = () => {
		if (!pictureRef.current) return { width: 0, height: 0 };
		const rect = pictureRef.current.getBoundingClientRect();
		return { width: Math.round(rect.width), height: Math.round(rect.height) };
	};

	useEffect(() => {
		setAutoSizes(getContainerSize());
	}, []);

	useDebouncedResize(() => {
		setAutoSizes(getContainerSize());
	}, 500);

	useEffect(() => {
		if (!autoSizes || !focalPoint) return;

		let intrinsicW: number | undefined;
		let intrinsicH: number | undefined;

		if (intrinsicSize) {
			intrinsicW = intrinsicSize.w;
			intrinsicH = intrinsicSize.h;
		} else if (width && height) {
			intrinsicW = width;
			intrinsicH = height;
		} else if (imageRatio) {
			intrinsicW = 500;
			intrinsicH = 500 * imageRatio;
		}
		if (!intrinsicW || !intrinsicH) return;

		const objectPositionStyle = computeFocalObjectPosition(focalPoint, intrinsicW, intrinsicH, autoSizes.width, autoSizes.height);

		setObjectPosition(objectPositionStyle);
	}, [autoSizes, focalPoint, width, height, imageRatio, intrinsicSize]);

	const shimmer = (w: number, h: number) => `
		<svg width="${w}" height="${h}" xmlns="http://www.w3.org/2000/svg">
			<defs>
				<linearGradient id="g">
					<stop stop-color="rgba(255,255,255,0)" offset="20%" />
					<stop stop-color="rgba(255,255,255,0.5)" offset="50%" />
					<stop stop-color="rgba(255,255,255,0)" offset="70%" />
				</linearGradient>
			</defs>
			<rect width="${w}" height="${h}" fill="rgba(0,0,0,0.2)" />
			<rect id="r" width="${w}" height="${h}" fill="url(#g)" />
			<animate xlink:href="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite" />
		</svg>
	`;
	const toBase64 = (str: string) => (typeof window === 'undefined' ? Buffer.from(str).toString('base64') : window.btoa(str));

	const biggestSize = autoSizes != null ? (autoSizes.width > autoSizes.height ? autoSizes.width + 'px' : autoSizes.height + 'px') : null;

	return (
		<picture
			ref={pictureRef}
			className={classNames(styles.Picture, imageCropping === 'contain' ? styles.Picture___contain : styles.Picture___cover, className, {
				[styles.Picture___round]: style === 'round',
			})}
		>
			<ImageOptimization
				className={styles.Picture_asset}
				src={url}
				alt={altText ?? ''}
				sizes={sizes ?? biggestSize ?? '4px'}
				width={width ?? (imageRatio ? 500 : undefined)}
				height={height ?? (imageRatio ? 500 * imageRatio : undefined)}
				style={{
					objectFit: imageCropping === 'contain' ? 'contain' : 'cover',
					objectPosition: imageCropping === 'contain' ? 'left center' : objectPosition,
				}}
				fetchpriority={fetchpriority}
				loading={lazyload ? 'lazy' : 'eager'}
				fill={ImageOptimization === 'img' ? null : width || height ? false : imageRatio ? false : true}
				placeholder={`data:image/svg+xml;base64,${toBase64(shimmer(500, 500))}`}
				onLoad={(e) => {
					const img = e.currentTarget as HTMLImageElement;
					if (img.naturalWidth && img.naturalHeight) {
						setIntrinsicSize({ w: img.naturalWidth, h: img.naturalHeight });
					}
				}}
			/>
		</picture>
	);
};
